import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Card, CardBody, Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Badge, Spinner, Alert } from 'reactstrap';
import { Plus, MapPin, Phone, Globe, Edit2, Trash2, Map as MapIcon, Copy, AlertTriangle, Camera, ImagePlus } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { motion, AnimatePresence, color } from 'framer-motion';
import { useSiteStore } from '../../../stores/Assets/site';
import { useSpaceStore } from '../../../stores/Assets/space';
import { PlanUpgradeModal } from '../../../Components/Common/PlanUpgradeModal';
import { useAuth } from '../../../stores/Auth/Auth';

const THEME_COLOR = '#c59253';
const THEME_COLOR_DARK = '#b98643ff';
const DEFAULT_CENTER = [25.2854, 51.5310];
const GRADIENT_BG = `linear-gradient(135deg, ${THEME_COLOR} 0%, ${THEME_COLOR_DARK} 100%)`;

const isValid = (lat, lon) => {
    const nLat = parseFloat(lat);
    const nLon = parseFloat(lon);
    return !isNaN(nLat) && !isNaN(nLon);
};

const customIcon = new L.Icon({
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
    shadowSize: [41, 41],
});

// Component to handle map centering and bounds
const GlobalViewManager = ({ sites, selectedSiteId }) => {
    const map = useMap();

    useEffect(() => {
        if (selectedSiteId) {
            // Focus on selected site
            const site = sites.find(s => s.site_id === selectedSiteId);
            if (site && isValid(site.latitude, site.longitude)) {
                map.flyTo([parseFloat(site.latitude), parseFloat(site.longitude)], 18, {
                    duration: 1.5
                });
            }
        } else {
            // Fit bounds to all sites (Global View)
            const validSites = sites.filter(s => isValid(s.latitude, s.longitude));
            if (validSites.length > 0) {
                const bounds = L.latLngBounds(validSites.map(s => [parseFloat(s.latitude), parseFloat(s.longitude)]));
                map.flyToBounds(bounds, { padding: [50, 50], duration: 1.5 });
            } else {
                map.flyTo(DEFAULT_CENTER, 11, { duration: 1.5 });
            }
        }
    }, [selectedSiteId, sites, map]);

    return null;
};

// Simple view changer for Modal Map
const ChangeView = ({ center, zoom }) => {
    const map = useMap();
    useEffect(() => {
        map.setView(center, zoom);
    }, [center, zoom, map]);
    return null;
};

const LocationStep = ({ sites, onNext }) => {
    const { createSite, updateSite, deleteSite } = useSiteStore();
    const { user } = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
    const [editingSite, setEditingSite] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [selectedSiteId, setSelectedSiteId] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [siteToDelete, setSiteToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const scrollContainerRef = useRef(null);
    const [formData, setFormData] = useState({
        address: '',
        phone: '',
        latitude: 25.2854,
        longitude: 51.5310,
        nameSuffix: '',
        logo: null,
        logoFile: null
    });

    const { getUserSpaces } = useSpaceStore();
    const [spacesMap, setSpacesMap] = useState({});

    const [loadingSpaces, setLoadingSpaces] = useState(true);

    // Fetch spaces to show counts on cards
    useEffect(() => {
        const fetchSpaces = async () => {
            setLoadingSpaces(true);
            try {
                const allSpaces = await getUserSpaces(1, 100);
                const grouped = {};
                (allSpaces || []).forEach(space => {
                    const sId = space.site_id;
                    if (!grouped[sId]) grouped[sId] = [];
                    grouped[sId].push(space);
                });

                setSpacesMap(grouped);
            } catch (err) {
                console.error("Failed to fetch spaces for Branch step", err);
            } finally {
                setLoadingSpaces(false);
            }
        };
        fetchSpaces();
    }, [getUserSpaces]);

    // Auto-select first site if none selected or invalid
    useEffect(() => {
        if (sites.length > 0) {
            const isSelectedValid = sites.some(s => s.site_id === selectedSiteId);
            if (!selectedSiteId || !isSelectedValid) {
                setSelectedSiteId(sites[0].site_id);
            }
        }
    }, [sites]);

    // Scroll to selected site
    useEffect(() => {
        if (selectedSiteId && scrollContainerRef.current) {
            const element = document.getElementById(`site-card-${selectedSiteId}`);
            if (element) {
                const container = scrollContainerRef.current;
                // Calculate position relative to container
                // offsetTop is relative to the closest positioned ancestor. 
                // We ensured container has position: relative.
                const elementTop = element.offsetTop;
                const elementHeight = element.clientHeight;
                const containerHeight = container.clientHeight;

                // Calculate scroll position to display the element in the center of the container
                const scrollTo = elementTop - (containerHeight / 2) + (elementHeight / 2);

                container.scrollTo({
                    top: scrollTo,
                    behavior: 'smooth'
                });
            }
        }
    }, [selectedSiteId]);

    const toggleModal = () => {
        setIsModalOpen(!isModalOpen);
        if (isModalOpen) setEditingSite(null);
    };

    const toggleUpgradeModal = () => {
        setIsUpgradeModalOpen(!isUpgradeModalOpen);
    };

    const handleAddClick = () => {
        const maxSites = user?.subscription?.plan?.max_sites || 100;
        if (sites.length >= maxSites) {
            setIsUpgradeModalOpen(true);
            return;
        }

        setFormData({
            address: '',
            phone: '',
            latitude: 25.2854,
            longitude: 51.5310,
            nameSuffix: '',
            logo: null,
            logoFile: null
        });
        setEditingSite(null);
        setIsModalOpen(true);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({
                    ...prev,
                    logoFile: file,
                    logo: reader.result
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const triggerFileInput = (site = null) => {
        if (site) {
            setEditingSite(site);
            setFormData({
                address: site.address || '',
                phone: site.phone || '',
                latitude: parseFloat(site.latitude) || DEFAULT_CENTER[0],
                longitude: parseFloat(site.longitude) || DEFAULT_CENTER[1],
                nameSuffix: site.name.split(' - ')[1] || '',
                logo: site.logo || null,
                logoFile: null
            });
            setIsModalOpen(true);
        }
        setTimeout(() => {
            document.getElementById('branch-logo-input').click();
        }, 100);
    };

    const handleDuplicateClick = (site, e) => {
        e.stopPropagation();

        const maxSites = user?.subscription?.plan?.max_sites || 100;
        if (sites.length >= maxSites) {
            setIsUpgradeModalOpen(true);
            return;
        }

        setEditingSite(null);
        setFormData({
            address: site.address || '',
            phone: site.phone || '',
            latitude: parseFloat(site.latitude) || DEFAULT_CENTER[0],
            longitude: parseFloat(site.longitude) || DEFAULT_CENTER[1],
            nameSuffix: (site.name.split(' - ')[1] || 'Copy') + ' (Copy)',
            logo: site.logo || null,
            logoFile: null
        });
        setIsModalOpen(true);
    };

    const handleEditClick = (site, e) => {
        e.stopPropagation();
        setEditingSite(site);
        setFormData({
            address: site.address || '',
            phone: site.phone || '',
            latitude: parseFloat(site.latitude) || DEFAULT_CENTER[0],
            longitude: parseFloat(site.longitude) || DEFAULT_CENTER[1],
            nameSuffix: site.name.split(' - ')[1] || '',
            logo: site.logo || null,
            logoFile: null
        });
        setIsModalOpen(true);
    };

    const handleDeleteClick = (site, e) => {
        e.stopPropagation();
        if (deleting) return;
        setSiteToDelete(site);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (siteToDelete) {
            setDeleting(true);
            try {
                await deleteSite(siteToDelete.site_id);
                setIsDeleteModalOpen(false);
                setSiteToDelete(null);
            } catch (err) {
                console.error("Deletion failed", err);
            } finally {
                setDeleting(false);
            }
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const finalName = formData.nameSuffix
            const data = new FormData();
            data.append('name', finalName);
            data.append('address', formData.address);
            data.append('phone', formData.phone);
            data.append('latitude', isValid(formData.latitude, formData.longitude) ? parseFloat(formData.latitude) : 0);
            data.append('longitude', isValid(formData.latitude, formData.longitude) ? parseFloat(formData.longitude) : 0);

            if (formData.logoFile) {
                data.append('file', formData.logoFile);
            } else if (formData.logo && !formData.logo.startsWith('data:')) {
                // If it's a URL (not base64 data uri), send it as image_url for duplication
                data.append('image_url', formData.logo);
            }

            if (editingSite) {
                await updateSite(editingSite.site_id, data);
            } else {
                await createSite(data);
            }
            toggleModal();
        } catch (error) {
            console.error("Failed to save site:", error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCardClick = (siteId) => {
        setSelectedSiteId(siteId);
    };

    const handleMarkerClick = (site) => {
        setSelectedSiteId(site.site_id);
    };

    return (
        <div className="p-3 p-lg-4 pb-0 h-100">
            <PlanUpgradeModal isOpen={isUpgradeModalOpen} toggle={toggleUpgradeModal} />

            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="fw-bold mb-1">Branches Manager</h4>
                    <p className="text-muted small">Manage your business branches and their geographical pins.</p>
                </div>
                <input
                    type="file"
                    id="branch-logo-input"
                    className="d-none"
                    accept="image/*"
                    onChange={handleFileChange}
                />
                <Button
                    style={{ backgroundColor: "#c59253" }}
                    className="rounded-pill px-4 d-flex align-items-center gap-2 shadow-sm border-0"
                    onClick={handleAddClick}
                >
                    <Plus size={18} /> Add Branch
                </Button>
            </div>

            <Row className="g-4 h-100">
                {/* Site Cards - Scrollable List */}
                <Col lg={6} className="h-100">
                    <div
                        ref={scrollContainerRef}
                        className="d-flex flex-column gap-2 pe-2 custom-scrollbar "
                        style={{
                            maxHeight: 'calc(100vh - 200px)',
                            overflowY: 'auto',
                            paddingBottom: '2rem',
                            paddingTop: '1rem',
                            scrollBehavior: 'smooth',
                            position: 'relative'
                        }}
                    >
                        <Button
                            style={{ borderColor: THEME_COLOR, border: `2px ${THEME_COLOR} dotted`, color: THEME_COLOR }}
                            className="rounded-4 w-100 d-flex align-items-center justify-content-center gap-2 mb-3 py-3 fw-bold shadow-sm  bg-transparent"
                            onClick={handleAddClick}
                        >
                            <Plus size={20} /> Add New Branch
                        </Button>

                        <AnimatePresence>
                            {sites.map((site) => {
                                const isSelected = site.site_id === selectedSiteId;
                                const siteSpaces = spacesMap[site.site_id] || [];

                                return (
                                    <motion.div
                                        id={`site-card-${site.site_id}`}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        transition={{ duration: 0.2 }}
                                        key={site.site_id}
                                        onClick={() => setSelectedSiteId(site.site_id)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <Card
                                            className={`border-0 rounded-4 overflow-hidden mb-3 transition-all cursor-pointer ${isSelected ? 'shadow-lg' : 'shadow-sm'}`}
                                            style={{
                                                background: isSelected ? GRADIENT_BG : 'white',
                                                border: isSelected ? 'none' : '1px solid #f1f5f9'
                                            }}
                                        >
                                            <CardBody className="p-3">
                                                <div className="d-flex justify-content-between">
                                                    <div className="me-3 position-relative">
                                                        <div
                                                            className={`rounded-3 overflow-hidden d-flex align-items-center justify-content-center ${isSelected ? 'bg-white bg-opacity-20' : 'bg-light'}`}
                                                            style={{ width: '56px', height: '56px', border: isSelected ? '1px solid rgba(255,255,255,0.2)' : '1px solid #e2e8f0' }}
                                                        >
                                                            {site.image_url ? (
                                                                <img src={site.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                            ) : (
                                                                <Camera size={20} className={isSelected ? 'text-white opacity-50' : 'text-muted'} />
                                                            )}

                                                            <div
                                                                className="position-absolute bottom-0 end-0 bg-primary rounded-circle d-flex align-items-center justify-content-center text-white shadow-sm border border-white"
                                                                style={{ width: '20px', height: '20px', cursor: 'pointer', transform: 'translate(4px, 4px)' }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    triggerFileInput(site);
                                                                }}
                                                            >
                                                                <Plus size={12} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex-grow-1 overflow-hidden">
                                                        <h6 className={`fw-bold mb-1 d-flex align-items-center gap-2 ${isSelected ? 'text-white' : 'text-dark'}`}>
                                                            {site.name}
                                                            {!isSelected && <span className="badge bg-light text-muted fw-normal" style={{ fontSize: '10px' }}>ID: {site.site_id}</span>}
                                                        </h6>
                                                        {site.address && (
                                                            <p className={`small mb-1 text-truncate ${isSelected ? 'text-white opacity-75' : 'text-secondary'}`}>
                                                                <MapPin size={12} className="me-1" /> {site.address}
                                                            </p>
                                                        )}
                                                        {site.phone && (
                                                            <p className={`small mb-1 ${isSelected ? 'text-white opacity-75' : 'text-secondary'}`}>
                                                                <Phone size={12} className="me-1" /> {site.phone}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="d-flex gap-1 align-items-start">
                                                        <Button color="link" className={`p-1 opacity-50 hover-opacity-100 ${isSelected ? 'text-white' : 'text-muted'}`} onClick={(e) => handleEditClick(site, e)}>
                                                            <Edit2 size={14} />
                                                        </Button>
                                                        <Button color="link" className={`p-1 opacity-50 hover-opacity-100 ${isSelected ? 'text-white' : 'text-danger'}`} onClick={(e) => handleDeleteClick(site, e)}>
                                                            <Trash2 className="fw-bold" size={14} />
                                                        </Button>
                                                    </div>
                                                </div>

                                                <div className="d-flex justify-content-between align-items-center mt-2">
                                                    <div className="d-flex gap-2">
                                                        <Button color="link" className={`p-0 small fw-bold text-decoration-none ${isSelected ? 'text-white' : 'text-primary'}`} style={{ fontSize: '11px' }} onClick={(e) => handleDuplicateClick(site, e)}>
                                                            <Copy size={12} className="me-1" /> Duplicate
                                                        </Button>
                                                    </div>
                                                    <Button color="link"
                                                        className={`p-0 small fw-bold text-decoration-none ${isSelected ? 'text-white' : 'text-muted'}`}
                                                        style={{ fontSize: '11px' }}
                                                        onClick={() => setSelectedSiteId(isSelected ? null : site.site_id)}
                                                    >
                                                        {isSelected ? 'Collapse' : 'Manage Spaces'}
                                                    </Button>
                                                </div>

                                                {isSelected && (
                                                    <div className='d-flex justify-content-between align-items-center mt-3 pt-2' style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                                                        <div className="d-flex gap-1 align-items-center overflow-hidden" style={{ maxWidth: '70%' }}>
                                                            {loadingSpaces ? (
                                                                <div className="d-flex gap-1">
                                                                    <div className="bg-white bg-opacity-25 rounded placeholder-glow" style={{ width: 40, height: 16 }}></div>
                                                                    <div className="bg-white bg-opacity-25 rounded placeholder-glow" style={{ width: 40, height: 16 }}></div>

                                                                </div>
                                                            ) : siteSpaces.length > 0 ? (
                                                                <>
                                                                    {siteSpaces.slice(0, 2).map((space, idx) => (
                                                                        <Badge
                                                                            key={space.space_id || idx}
                                                                            color="light"
                                                                            className="bg-white text-dark border-0 fw-medium shadow-sm"
                                                                            style={{ fontSize: '10px', padding: '4px 8px' }}
                                                                        >
                                                                            {space.name}
                                                                        </Badge>
                                                                    ))}
                                                                    {siteSpaces.length > 2 && (
                                                                        <span className="small text-white opacity-75 ms-1" style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>
                                                                            + {siteSpaces.length - 2} more
                                                                        </span>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <span className="small text-white opacity-50" style={{ fontSize: '11px', fontStyle: 'italic' }}>
                                                                    No spaces yet
                                                                </span>
                                                            )}
                                                        </div>
                                                        <Button
                                                            size="sm"
                                                            style={{ color: "#c59253", fontWeight: "bold", fontSize: '11px' }}
                                                            className="bg-white border-0 rounded-3 px-3 shadow-sm py-1"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onNext(site.site_id);
                                                            }}
                                                        >
                                                            Manage Spaces
                                                        </Button>
                                                    </div>
                                                )}
                                            </CardBody>
                                        </Card>
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>
                        {sites.length === 0 && (
                            <div className="text-center py-5 bg-white rounded-4 border-2 border-dashed">
                                <MapIcon size={48} className="text-muted mb-3 opacity-25" />
                                <h5 className="text-muted">No Branches added yet</h5>
                                <p className="text-muted small">Click "Add Branch" to register your first branch.</p>
                            </div>
                        )}
                    </div>
                </Col>

                {/* Global Map - Fixed Height */}
                <Col lg={6} className="h-100">
                    <Card className="border-0 shadow-sm rounded-4 h-100 sticky-top overflow-hidden" style={{ minHeight: '500px', maxHeight: 'calc(100vh - 180px)' }}>
                        <CardBody className="p-0 position-relative h-100">
                            <div className="position-absolute top-0 start-0 w-100 p-3 d-flex justify-content-center" style={{ zIndex: 1000, pointerEvents: 'none' }}>
                                <Button
                                    color="white"
                                    className="shadow-sm py-2 px-4 rounded-pill text-dark d-flex align-items-center gap-2 border fw-bold hover-up"
                                    style={{ pointerEvents: 'auto' }}
                                    onClick={() => setSelectedSiteId(null)}
                                >
                                    <Globe size={16} className="text-primary" />
                                    Global View
                                </Button>
                            </div>
                            <MapContainer
                                center={[25.2854, 51.5310]}
                                zoom={11}
                                style={{ height: '100%', width: '100%' }}
                            >
                                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                <GlobalViewManager sites={sites} selectedSiteId={selectedSiteId} />
                                {sites.filter(s => isValid(s.latitude, s.longitude)).map((site) => (
                                    <Marker
                                        key={site.site_id}
                                        position={[parseFloat(site.latitude), parseFloat(site.longitude)]}
                                        icon={customIcon}
                                        eventHandlers={{
                                            click: () => handleMarkerClick(site)
                                        }}
                                    >
                                        <Popup>
                                            <strong>{site.name}</strong><br />
                                            {site.address}
                                        </Popup>
                                    </Marker>
                                ))}
                            </MapContainer>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Add/Edit Modal */}
            <Modal isOpen={isModalOpen} toggle={toggleModal} centered size="lg">
                <ModalHeader toggle={toggleModal} className="border-0 pb-0">
                    <div className="fw-bold fs-5">{editingSite ? 'Edit Branch' : 'Add New Branch'}</div>
                </ModalHeader>
                <ModalBody className="p-4">
                    <Form>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="small fw-bold">Branch Name</Label>
                                    <Input
                                        type="text"
                                        placeholder="Branch identifier..."
                                        value={formData.nameSuffix}
                                        onChange={(e) => setFormData({ ...formData, nameSuffix: e.target.value })}
                                        className="rounded-3 border-2"
                                    />
                                    <div className="text-muted mt-1" style={{ fontSize: '11px' }}>
                                        Example: "My Business - Downtown"
                                    </div>
                                </FormGroup>
                                <FormGroup>
                                    <Label className="small fw-bold">Branch Logo</Label>
                                    <div
                                        className="rounded-3 border-2 border-dashed d-flex flex-column align-items-center justify-content-center p-3 cursor-pointer hover-bg-light"
                                        style={{ height: '120px', backgroundColor: '#f8fafc', borderColor: '#e2e8f0' }}
                                        onClick={() => triggerFileInput()}
                                    >
                                        {formData.logo ? (
                                            <div className="position-relative h-100 w-100 d-flex align-items-center justify-content-center">
                                                <img src={formData.logo} alt="Preview" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                                                <Button
                                                    size="sm"
                                                    color="danger"
                                                    className="position-absolute top-0 end-0 rounded-circle p-1"
                                                    style={{ width: '24px', height: '24px', transform: 'translate(10px, -10px)' }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setFormData({ ...formData, logo: null, logoFile: null });
                                                    }}
                                                >
                                                    <Plus size={14} style={{ transform: 'rotate(45deg)' }} />
                                                </Button>
                                            </div>
                                        ) : (
                                            <>
                                                <ImagePlus size={32} className="text-muted mb-2 opacity-50" />
                                                <span className="small text-muted">Click to upload logo</span>
                                            </>
                                        )}
                                    </div>
                                </FormGroup>
                                <FormGroup>
                                    <Label className="small fw-bold">Full Address</Label>
                                    <Input
                                        type="text"
                                        placeholder="Street, Building, Zone..."
                                        value={formData.address}
                                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                        className="rounded-3 border-2"
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label className="small fw-bold">Phone Number</Label>
                                    <Input
                                        type="tel"
                                        placeholder="+974 ..."
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        className="rounded-3 border-2"
                                    />
                                </FormGroup>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="small fw-bold">Latitude</Label>
                                            <Input
                                                type="number"
                                                step="any"
                                                value={isNaN(formData.latitude) ? '' : formData.latitude}
                                                onChange={(e) => setFormData({ ...formData, latitude: e.target.value === '' ? NaN : parseFloat(e.target.value) })}
                                                className="rounded-3 border-2"
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="small fw-bold">Longitude</Label>
                                            <Input
                                                type="number"
                                                step="any"
                                                value={isNaN(formData.longitude) ? '' : formData.longitude}
                                                onChange={(e) => setFormData({ ...formData, longitude: e.target.value === '' ? NaN : parseFloat(e.target.value) })}
                                                className="rounded-3 border-2"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </Col>
                            <Col md={6}>
                                <Label className="small fw-bold mb-2">Pin Branch on Map</Label>
                                <div className="rounded-3 overflow-hidden border" style={{ height: '320px' }}>
                                    <MapContainer
                                        center={isValid(formData.latitude, formData.longitude) ? [formData.latitude, formData.longitude] : DEFAULT_CENTER}
                                        zoom={13}
                                        style={{ height: '100%', width: '100%' }}
                                    >
                                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                        {isValid(formData.latitude, formData.longitude) && (
                                            <Marker
                                                position={[formData.latitude, formData.longitude]}
                                                icon={customIcon}
                                                draggable={true}
                                                eventHandlers={{
                                                    dragend: (e) => {
                                                        const { lat, lng } = e.target.getLatLng();
                                                        setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
                                                    }
                                                }}
                                            />
                                        )}
                                        {isValid(formData.latitude, formData.longitude) && (
                                            <ChangeView
                                                center={[formData.latitude, formData.longitude]} zoom={13} />
                                        )}
                                    </MapContainer>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </ModalBody>
                <ModalFooter className="border-0 pt-0">
                    <Button color="light" className="rounded-pill px-4" onClick={toggleModal} disabled={submitting}>Cancel</Button>
                    <Button color="primary" className="rounded-pill px-4 shadow-sm" onClick={handleSubmit} disabled={submitting}>
                        {submitting ? <><Spinner size="sm" className="me-2" /> Saving...</> : (editingSite ? 'Save Changes' : 'Register Branch')}
                    </Button>
                </ModalFooter>
            </Modal>

            <style>
                {`
                    .card-animate:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1) !important;
                    }
                    /* Custom scrollbar for better aesthetics */
                    .custom-scrollbar::-webkit-scrollbar {
                        width: 6px;
                    }
                    .custom-scrollbar::-webkit-scrollbar-track {
                        background: rgba(0,0,0,0.05);
                        border-radius: 4px;
                    }
                    .custom-scrollbar::-webkit-scrollbar-thumb {
                        background: rgba(0,0,0,0.1);
                        border-radius: 4px;
                    }
                    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                        background: rgba(0,0,0,0.2);
                    }
                `}
            </style>
            {/* Delete Confirmation Modal */}
            <Modal isOpen={isDeleteModalOpen} toggle={() => setIsDeleteModalOpen(false)} centered className="modal-dialog-centered">
                <ModalBody className="p-4 text-center">
                    <div className="mb-4">
                        <div className="rounded-circle bg-danger bg-opacity-10 d-flex align-items-center justify-content-center mx-auto" style={{ width: '70px', height: '70px' }}>
                            <AlertTriangle size={32} className="text-danger" />
                        </div>
                    </div>
                    <h4 className="fw-bold mb-2">Delete Branch?</h4>
                    <p className="text-muted mb-4">
                        Are you sure you want to delete <span className="fw-bold text-dark">"{siteToDelete?.name}"</span>?
                        This will also remove all associated spaces and data. This action cannot be undone.
                    </p>
                    <div className="d-flex gap-2 justify-content-center">
                        <Button
                            color="light"
                            className="rounded-pill px-4"
                            onClick={() => setIsDeleteModalOpen(false)}
                            disabled={deleting}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="danger"
                            className="rounded-pill px-4 border-0 shadow-sm d-flex align-items-center gap-2"
                            onClick={confirmDelete}
                            disabled={deleting}
                        >
                            {deleting ? (
                                <>
                                    <Spinner size="sm" color="white" /> Deleting...
                                </>
                            ) : (
                                "Yes, Delete Branch"
                            )}
                        </Button>
                    </div>
                </ModalBody>
            </Modal>
        </div>
    );
};

export default LocationStep;
